import { Flex, MenuList, Text } from '@chakra-ui/react';
import {
  Badge,
  Button,
  ButtonText,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem
} from '@morressier/alembic';
import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { EmailTemplateDoc } from '@publishing-engine/domain/models/email-templates';

interface Props {
  templates: EmailTemplateDoc[];
  selectedIndex: number | null;
  onSelectTemplate: (index: number) => void;
  onOpenDetailView: () => void;
}

const getTagFromPhase = (tag: string) => {
  switch (tag) {
    case 'sub':
      return 'Original Submission';
    case 'rev':
      return 'Revision';
    case 'resub':
      return 'Resubmission';
    default:
      return '';
  }
};

const BrowseTemplatesMenu = ({
  templates,
  selectedIndex,
  onSelectTemplate,
  onOpenDetailView
}: Props) => {
  const [search, setSearch] = useState('');

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  return (
    <Menu ariaLabel="browseTemplates">
      <MenuButton as={Button} variant="secondary">
        Browse Templates
      </MenuButton>

      <MenuList px={2}>
        <Flex justifyContent="space-between" mb={2}>
          <Text textStyle="headingItem">
            {`Browse Templates (${templates.length})`}
          </Text>
          <ButtonText onClick={onOpenDetailView}>
            Templates Detail View
          </ButtonText>
        </Flex>

        <Input
          name="search"
          iconLeft={<FontAwesomeIcon icon={faSearch} />}
          placeholder="Search template by title"
          mb={2}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {filteredTemplates.length === 0 && (
          <Text p={2} color="gray.500">
            No templates found
          </Text>
        )}

        {filteredTemplates.map(template => {
          const index = templates.findIndex(t => t._id === template._id);
          const isSelected = index === selectedIndex;

          return (
            <div key={template._id}>
              <MenuItem
                ariaLabel={template.name}
                onClick={() => onSelectTemplate(index)}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  {template.name}
                  {isSelected && (
                    <Text color="green.500">
                      <FontAwesomeIcon size="lg" icon={faCircleCheck} />
                    </Text>
                  )}
                </Flex>

                {template.phases.length > 0 && (
                  <Flex gap={1}>
                    {template.phases.map(phase => (
                      <Badge key={phase} variant="neutral">
                        {getTagFromPhase(phase)}
                      </Badge>
                    ))}
                    {template.default && (
                      <Badge variant="neutral">Default</Badge>
                    )}
                  </Flex>
                )}
              </MenuItem>
              <MenuDivider />
            </div>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export default BrowseTemplatesMenu;
